import { Popconfirm } from 'antd';
import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/app.hooks';
import _ from 'lodash';
import {
  FilterByDateSwap,
  FilterWithSwapOption,
} from '../../../../common/components/DataTable/DataTableFilters';
import { IMainTable } from '../../../../common/models/common';
import { ISearch } from '../../../../services/o365/o365-copilot-usage/o365-copilot-usage.model';
import { useHistory } from 'react-router-dom';
import DataTable from '../../../../common/components/DataTable';
import ability, { Can } from '../../../../common/ability';
import { Action, Page } from '../../../../common/constants/pageAction';
import {
  deleteO365CopilotUsage,
  searchO365CopilotUsage,
} from '../../../../store/o365/o365-copilot-usage/o365-copilot-usage.action';
import {
  clearO365CopilotUsageMessages,
  o365CopilotUsageSelector,
  setTableColumnSelection,
} from '../../../../store/o365/o365-copilot-usage/o365-copilot-usage.reducer';
import o365CopilotUsageService from '../../../../services/o365/o365-copilot-usage/o365-copilot-usage.service';
import { showDateFromApi } from '../../../../common/helperFunction';

const MainTable: React.ForwardRefRenderFunction<unknown, IMainTable> = (props, ref) => {
  const {
    setSelectedId,
    setShowSelectedListModal,
    setValuesForSelection,
    isMultiple,
    setFilterKeys,
    tableButtons,
  } = props;
  const o365CopilotUsage = useAppSelector(o365CopilotUsageSelector);
  const dispatch = useAppDispatch();
  const dataTableRef = useRef(null);
  const history = useHistory();
  const [ObjectForColumnFilter, setObjectForColumnFilter] = useState({});

  useImperativeHandle(ref, () => ({
    refreshData() {
      dataTableRef?.current.refreshData();
    },
  }));

  useEffect(() => {
    if (isMultiple) {
      dataTableRef?.current.getValuesForSelection();
    }
  }, [isMultiple]);

  const exportExcelFile = (searchData: ISearch) => {
    return o365CopilotUsageService.exportExcelFile(searchData);
  };

  const FilterBySwap = (dataIndex: string, form, isRelativeDropDownFlag?: boolean) => {
    setFilterKeys(ObjectForColumnFilter);
    return FilterWithSwapOption(
      dataIndex,
      o365CopilotUsage.search.tableName,
      form,
      null,
      ObjectForColumnFilter,
      null,
      null,
      isRelativeDropDownFlag
    );
  };

  const FilterByDateSwapTable = (dataIndex: string, tableName: string, form: any) => {
    return FilterByDateSwap(dataIndex, tableName, form, null, ObjectForColumnFilter);
  };

  const getTableColumns = (form) => {
    return [
      {
        title: <span className="dragHandler">ID</span>,
        column: 'id',
        sorter: true,
        ellipsis: true,
        children: [
          {
            title: FilterBySwap('id', form),
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Tenant Name</span>,
        column: 'TenantId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('tenant_id', form, true),
            dataIndex: 'tenant_name',
            key: 'tenant_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Company Name</span>,
        column: 'CompanyId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('company_id', form, true),
            dataIndex: 'company_name',
            key: 'company_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">BU Name</span>,
        column: 'BU_Id',
        sorter: true,
        children: [
          {
            title: FilterBySwap('bu_id', form, true),
            dataIndex: 'bu_name',
            key: 'bu_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Date Added</span>,
        column: 'Date Added',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable('date_added', o365CopilotUsage.search.tableName, form),
            dataIndex: 'date_added',
            key: 'date_added',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Report Refresh Date</span>,
        column: 'Report Refresh Date',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'report_refresh_date',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'report_refresh_date',
            key: 'report_refresh_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">User Principal Name</span>,
        column: 'User Principal Name',
        sorter: true,
        children: [
          {
            title: FilterBySwap('user_principal_name', form),
            dataIndex: 'user_principal_name',
            key: 'user_principal_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Display Name</span>,
        column: 'Display Name',
        sorter: true,
        children: [
          {
            title: FilterBySwap('display_name', form),
            dataIndex: 'display_name',
            key: 'display_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity Date</span>,
        column: 'Last Activity Date',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date',
            key: 'last_activity_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Report Period</span>,
        column: 'Report Period',
        sorter: true,
        children: [
          {
            title: FilterBySwap('report_period', form),
            dataIndex: 'report_period',
            key: 'report_period',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Prompts Submitted (All Apps)</span>,
        column: 'Prompts Submitted (All Apps)',
        sorter: true,
        children: [
          {
            title: FilterBySwap('prompts_submitted_for_all_apps', form),
            dataIndex: 'prompts_submitted_for_all_apps',
            key: 'prompts_submitted_for_all_apps',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Prompts (Copilot Chat Work)</span>,
        column: 'Prompts (Copilot Chat Work)',
        sorter: true,
        children: [
          {
            title: FilterBySwap('prompts_submitted_for_copilot_chat_work', form),
            dataIndex: 'prompts_submitted_for_copilot_chat_work',
            key: 'prompts_submitted_for_copilot_chat_work',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Prompts (Copilot Chat Web)</span>,
        column: 'Prompts (Copilot Chat Web)',
        sorter: true,
        children: [
          {
            title: FilterBySwap('prompts_submitted_for_copilot_chat_web', form),
            dataIndex: 'prompts_submitted_for_copilot_chat_web',
            key: 'prompts_submitted_for_copilot_chat_web',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Active Usage Days (All Apps)</span>,
        column: 'Active Usage Days (All Apps)',
        sorter: true,
        children: [
          {
            title: FilterBySwap('active_usage_days_for_all_apps', form),
            dataIndex: 'active_usage_days_for_all_apps',
            key: 'active_usage_days_for_all_apps',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Copilot Chat Work)</span>,
        column: 'Last Activity (Copilot Chat Work)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_copilot_chat_work_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_copilot_chat_work_utc',
            key: 'last_activity_date_of_copilot_chat_work_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Copilot Chat Web)</span>,
        column: 'Last Activity (Copilot Chat Web)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_copilot_chat_web_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_copilot_chat_web_utc',
            key: 'last_activity_date_of_copilot_chat_web_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Teams Copilot)</span>,
        column: 'Last Activity (Teams Copilot)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_teams_copilot_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_teams_copilot_utc',
            key: 'last_activity_date_of_teams_copilot_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Word Copilot)</span>,
        column: 'Last Activity (Word Copilot)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_word_copilot_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_word_copilot_utc',
            key: 'last_activity_date_of_word_copilot_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Excel Copilot)</span>,
        column: 'Last Activity (Excel Copilot)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_excel_copilot_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_excel_copilot_utc',
            key: 'last_activity_date_of_excel_copilot_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (PowerPoint Copilot)</span>,
        column: 'Last Activity (PowerPoint Copilot)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_powerpoint_copilot_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_powerpoint_copilot_utc',
            key: 'last_activity_date_of_powerpoint_copilot_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Outlook Copilot)</span>,
        column: 'Last Activity (Outlook Copilot)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_outlook_copilot_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_outlook_copilot_utc',
            key: 'last_activity_date_of_outlook_copilot_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (OneNote Copilot)</span>,
        column: 'Last Activity (OneNote Copilot)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_onenote_copilot_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_onenote_copilot_utc',
            key: 'last_activity_date_of_onenote_copilot_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Loop Copilot)</span>,
        column: 'Last Activity (Loop Copilot)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_loop_copilot_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_loop_copilot_utc',
            key: 'last_activity_date_of_loop_copilot_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (M365 Copilot App)</span>,
        column: 'Last Activity (M365 Copilot App)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_microsoft_365_copilot_app_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_microsoft_365_copilot_app_utc',
            key: 'last_activity_date_of_microsoft_365_copilot_app_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Edge Copilot)</span>,
        column: 'Last Activity (Edge Copilot)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_edge_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_edge_utc',
            key: 'last_activity_date_of_edge_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Last Activity (Copilot Agent)</span>,
        column: 'Last Activity (Copilot Agent)',
        sorter: true,
        children: [
          {
            title: FilterByDateSwap(
              'last_activity_date_of_copilot_agent_utc',
              o365CopilotUsage.search.tableName,
              form,
              null,
              ObjectForColumnFilter
            ),
            dataIndex: 'last_activity_date_of_copilot_agent_utc',
            key: 'last_activity_date_of_copilot_agent_utc',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
    ];
  };

  const removeO365CopilotUsage = (id: number) => {
    dispatch(deleteO365CopilotUsage(id));
  };

  const tableAction = (_, data: any) => (
    <div className="btns-block">
      <Can I={Action.Update} a={Page.O365CopilotUsage}>
        <a
          className="action-btn"
          onClick={() => {
            setSelectedId(data.id);
            history.push(`/o365/o365-copilot-usage/${data.id}`);
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/assets/images/ic-edit.svg`} alt="" />
        </a>
      </Can>
      <Can I={Action.Delete} a={Page.O365CopilotUsage}>
        <Popconfirm title="Delete Record?" onConfirm={() => removeO365CopilotUsage(data.id)}>
          <a href="#" title="" className="action-btn">
            <img src={`${process.env.PUBLIC_URL}/assets/images/ic-delete.svg`} alt="" />
          </a>
        </Popconfirm>
      </Can>
    </div>
  );

  return (
    <>
      <DataTable
        ref={dataTableRef}
        showAddButton={ability.can(Action.Add, Page.O365CopilotUsage)}
        setSelectedId={setSelectedId}
        tableAction={tableAction}
        exportExcelFile={exportExcelFile}
        getTableColumns={getTableColumns}
        reduxSelector={o365CopilotUsageSelector}
        searchTableData={searchO365CopilotUsage}
        clearTableDataMessages={clearO365CopilotUsageMessages}
        setTableColumnSelection={setTableColumnSelection}
        setShowSelectedListModal={setShowSelectedListModal}
        setValuesForSelection={setValuesForSelection}
        showBulkUpdate={ability.can(Action.Update, Page.O365CopilotUsage)}
        setObjectForColumnFilter={setObjectForColumnFilter}
        tableButtons={tableButtons}
      />
    </>
  );
};

export default forwardRef(MainTable);
